import { useEffect, useState } from "react";

import { api } from "../../lib/http.ts";
import type { ReleaseNote } from "./releaseNotes.ts";

const SEEN_KEY = "sally.whatsnew.seen";

/** Fire this anywhere to reopen the What's-new popup (e.g. a footer link). */
export const WHATS_NEW_EVENT = "sally:whats-new";

/**
 * Auto-shows the latest release note once per new version (tracked in
 * localStorage), and reopens on the WHATS_NEW_EVENT window event. Mount once,
 * globally (in the app layout).
 */
export function WhatsNewModal() {
  const [open, setOpen] = useState(false);
  // Only admin-approved notes are shown; pending ones stay hidden until approved.
  const [notes, setNotes] = useState<ReleaseNote[]>([]);

  useEffect(() => {
    let cancelled = false;
    // Server returns the visible (admin-approved) notes with any admin edits applied.
    api<{ notes: ReleaseNote[] }>("/whats-new")
      .then((r) => {
        if (cancelled) return;
        const visible = r.notes ?? [];
        setNotes(visible);
        const latestId = visible[0]?.id ?? "";
        try {
          if (latestId && localStorage.getItem(SEEN_KEY) !== latestId) setOpen(true);
        } catch {
          /* localStorage unavailable — skip auto-show */
        }
      })
      .catch(() => {
        /* on error, show nothing rather than unapproved content */
      });
    const onOpen = () => setOpen(true);
    window.addEventListener(WHATS_NEW_EVENT, onOpen);
    return () => {
      cancelled = true;
      window.removeEventListener(WHATS_NEW_EVENT, onOpen);
    };
  }, []);

  function close() {
    try {
      localStorage.setItem(SEEN_KEY, notes[0]?.id ?? "");
    } catch {
      /* ignore */
    }
    setOpen(false);
  }

  if (!open) return null;
  if (notes.length === 0) return null;

  return (
    <div className="modal-back" style={{ zIndex: 1100 }} onClick={close}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3 className="modal-title">✨ What's new in SellSynq</h3>
            <p className="modal-sub">Updates to SellSynq — scroll for earlier versions.</p>
          </div>
        </div>

        <div className="modal-body" style={{ maxHeight: "60vh", overflowY: "auto" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {notes.map((r, idx) => (
              <div key={r.id}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                    marginBottom: 4,
                  }}
                >
                  <span
                    className="mono"
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: idx === 0 ? "#fff" : "var(--text-2)",
                      background: idx === 0 ? "var(--gc-blue, #1a73e8)" : "var(--bg-2)",
                      borderRadius: 10,
                      padding: "1px 8px",
                    }}
                  >
                    v{r.version}
                  </span>
                  <span style={{ fontSize: 12, color: "var(--text-3)" }}>{r.date}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-1)" }}>
                    {r.title}
                  </span>
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 6 }}>
                  {r.items.map((it, i) => (
                    <li key={i} style={{ fontSize: 13, color: "var(--text-2)", lineHeight: 1.5 }}>
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="modal-foot">
          <button type="button" className="pill-btn primary" onClick={close}>
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
